import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LayoutDashboard } from 'lucide-react';
import MfaVerifyPage from './MfaVerifyPage';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaState, setMfaState] = useState(null);
  const { login, googleLogin, setUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.mfa_required) { setMfaState(result); }
      else { toast.success('Logged in successfully'); navigate('/dashboard'); }
    } catch (error) {
      const data = error.response?.data;
      if (data?.mfa_required) { setMfaState(data); }
      else {
        const detail = data?.detail;
        toast.error(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e?.msg).join('. ') : 'Login failed');
      }
    } finally { setLoading(false); }
  };

  const handleMfaSuccess = (userData) => {
    if (userData.access_token) {
      try {
        localStorage.setItem('access_token', userData.access_token);
        localStorage.setItem('refresh_token', userData.refresh_token);
      } catch (e) {
        console.warn('Failed to persist auth tokens:', e);
      }
    }
    setUser(userData);
    navigate('/dashboard');
  };

  if (mfaState) return <MfaVerifyPage challengeId={mfaState.challenge_id} mfaMethod={mfaState.mfa_method} emailHint={mfaState.email_hint} phoneHint={mfaState.phone_hint} onSuccess={handleMfaSuccess} />;

  return (
    <div className="min-h-screen flex">
      {/* Left - Brand */}
      <div className="hidden lg:flex lg:w-[45%] flex-col justify-between p-14 relative overflow-hidden" style={{ background: '#0A3622' }}>
        <div className="relative z-10">
          <div className="flex items-center gap-2.5 mb-16">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <LayoutDashboard size={18} className="text-white" />
            </div>
            <span className="font-heading text-lg font-semibold text-white">SnuggleMind</span>
          </div>
          <h2 className="font-heading text-4xl font-bold text-white leading-tight mb-5">
            Close the books<br />
            <span style={{ color: '#D98A4A' }}>correctly</span>, not<br />
            just faster.
          </h2>
          <p className="text-[15px] font-body leading-relaxed max-w-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
            From categorization to close readiness in one workflow. Policy-aware, reconciliation-first bookkeeping for teams that need trust.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-6 font-body text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ background: '#2D7A5D' }}></div>Reconciled</div>
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ background: '#D98A4A' }}></div>Needs Review</div>
          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full" style={{ background: '#C84B31' }}></div>Policy Override</div>
        </div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-[0.08]" style={{ background: 'radial-gradient(circle, #D98A4A 0%, transparent 70%)' }}></div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-[55%] flex flex-col justify-center px-8 lg:px-16 xl:px-20" style={{ background: '#F7F6F2' }}>
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-2.5 mb-10 lg:hidden">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#0A3622' }}>
              <LayoutDashboard size={18} className="text-white" />
            </div>
            <span className="font-heading text-lg font-semibold" style={{ color: '#1A1A1A' }}>SnuggleMind</span>
          </div>

          <h1 className="font-heading text-3xl font-bold tracking-tight mb-1.5" style={{ color: '#1A1A1A' }}>Welcome back</h1>
          <p className="font-body text-[15px] mb-7" style={{ color: '#4B5563' }}>Sign in to your account</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[12px] font-body font-medium" style={{ color: '#4B5563' }}>Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required data-testid="login-email-input" className="h-11 rounded-lg font-body text-[14px]" style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }} />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[12px] font-body font-medium" style={{ color: '#4B5563' }}>Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-[12px] font-body font-medium hover:underline"
                  style={{ color: '#0A3622' }}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required data-testid="login-password-input" className="h-11 rounded-lg font-body text-[14px]" style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }} />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full h-11 rounded-lg font-body font-medium text-white text-[14px]" style={{ background: '#0A3622' }}>
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full" style={{ borderTop: '1px solid #E5E3DB' }}></div></div>
            <div className="relative flex justify-center text-[11px] uppercase"><span className="px-3 font-body tracking-wider" style={{ background: '#F7F6F2', color: '#9CA3AF' }}>or</span></div>
          </div>

          <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-login-button" className="w-full h-11 rounded-lg font-body font-medium text-[14px]" style={{ borderColor: '#E5E3DB', color: '#1A1A1A' }}>
            <svg className="mr-2" width="16" height="16" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <p className="mt-6 text-center text-[13px] font-body" style={{ color: '#4B5563' }}>
            Don't have an account? <Link to="/register" className="font-medium hover:underline" style={{ color: '#0A3622' }} data-testid="register-link">Create one</Link>
          </p>

          <div className="mt-6 flex items-center justify-center gap-3 font-body text-[11px]" style={{ color: '#9CA3AF' }}>
            <Link to="/terms" className="hover:underline">Terms</Link>
            <span>&middot;</span>
            <Link to="/privacy" className="hover:underline">Privacy</Link>
            <span>&middot;</span>
            <Link to="/subscription-agreement" className="hover:underline">Subscription</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
